import momentTimezone from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fromEvent } from 'rxjs';
import { buffer, debounceTime, filter, map, share, tap } from 'rxjs/operators';

import { PickedBox as PickedBoxInput, PickingProblem } from 'src/apollo/types/globalTypes';
import { StartPickingMutation_startPicking_rejectedBatches } from 'src/apollo/types/StartPickingMutation';
import { useStableCallback } from 'src/common/picking/hooks/useStableCallback';
import {
  BoxPickingItem,
  buildBoxPickingFinishParams,
  canJoinPreviousPackage,
  clearStoredBoxes,
  getBoxPickingStorageKey,
  getNextPackageId,
  getPackageNumbers,
  isRepeatedScan,
  joinLastBoxToPreviousPackage,
  LastScan,
  loadStoredBoxes,
  normalizeBoxBarcode,
  PickedBox,
  purgeExpiredStoredBoxes,
  restoreStoredBoxes,
  scanBox,
  storeBoxes,
  summarizeBoxPicking,
} from 'src/common/picking/logic/boxPicking';
import { playErrorSound } from 'src/common/picking/logic/sounds';
import { sanitizeScannedCode } from 'src/common/picking/organisms/pickingList/logic';
import { ProductsBySku } from 'src/common/types';
import { Button } from 'src/components/atoms/button';
import { CircularProgress } from 'src/components/atoms/circularProgress';
import {
  ActionButton,
  BoxPickingContainer,
  ButtonsContainer,
  CircularProgressContainer,
  JoinPackageRow,
  LastBoxInfo,
  MissingCell,
  ModalButtons,
  ModalContainer,
  ModalTitle,
  ProgressText,
  ScanArea,
  ScanHint,
  SectionTitle,
  StyledModal,
  Table,
} from './styles';

const SCAN_DEBOUNCE_MS = 70;
const MIN_SCANNED_CODE_LENGTH = 12;
const LIVUP_TIMEZONE = 'America/Sao_Paulo';
const SNACKBAR_DURATION_MS = 8000;
const REPEATED_SCAN_NOTICE =
  'A etiqueta já está na lista, lida há menos de 1 segundo. Se for outra caixa com a mesma etiqueta, leia de novo.';

const SCANNED_CHARACTER_REGEX = /^[A-Za-z0-9]$/;

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');

const blurFocusedElement = () => {
  const { activeElement } = document;
  if (activeElement instanceof HTMLElement && activeElement !== document.body && !isEditableTarget(activeElement)) {
    activeElement.blur();
  }
};

export interface BoxPickingFinishParams {
  boxes: PickedBoxInput[];
  pickingProblems: PickingProblem[];
}

export interface BoxPickingProps {
  order: string;
  distributionCenter: string;
  items: BoxPickingItem[];
  productsBySku: ProductsBySku;
  rejectedBatches: StartPickingMutation_startPicking_rejectedBatches[];
  minShelfLifePercent: number | null;
  finishingPicking: boolean;
  isExpressDelivery: boolean;
  isUrgent?: boolean;
  onFinish: (params: BoxPickingFinishParams) => void;
  onCancel: () => void;
  onForceCancel: () => void;
}

export const BoxPicking: React.FC<BoxPickingProps> = ({ ...props }) => {
  const { enqueueSnackbar } = useSnackbar();
  const storageKey = getBoxPickingStorageKey(props.distributionCenter, props.order);
  const rejectedBatchCodes = useMemo(() => new Set((props.rejectedBatches || []).map(batch => batch.code)), [
    props.rejectedBatches,
  ]);
  const [boxes, setBoxes] = useState<PickedBox[]>([]);
  const [lastScanRefused, setLastScanRefused] = useState(false);
  const lastScanRef = useRef<LastScan | null>(null);
  const scanInProgressRef = useRef(false);
  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const summary = useMemo(() => summarizeBoxPicking(props.items, boxes), [props.items, boxes]);
  const orderedTotal = summary.reduce((total, item) => total + item.orderedQuantity, 0);
  const pickedTotal = summary.reduce((total, item) => total + item.pickedQuantity, 0);
  const missingItems = summary.filter(item => item.missingQuantity > 0);
  const lastBox = boxes.length > 0 ? boxes[boxes.length - 1] : null;
  const joinTarget = canJoinPreviousPackage(boxes) ? boxes[boxes.length - 2] : null;
  const packageNumbers = useMemo(() => getPackageNumbers(boxes), [boxes]);
  const packageCount = packageNumbers.length > 0 ? packageNumbers[packageNumbers.length - 1] : 0;

  const getProductName = (sku: string) =>
    (props.productsBySku && props.productsBySku[sku] && props.productsBySku[sku].name) || sku;

  const showError = (message: string) => {
    playErrorSound();
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: SNACKBAR_DURATION_MS });
  };

  const updateBoxes = (updatedBoxes: PickedBox[]) => {
    setBoxes(updatedBoxes);
    storeBoxes(storageKey, updatedBoxes);
  };

  useEffect(() => {
    purgeExpiredStoredBoxes();
    const { boxes: restoredBoxes, droppedCount } = restoreStoredBoxes({
      storedBoxes: loadStoredBoxes(storageKey, props.items),
      items: props.items,
      rejectedBatchCodes,
      minShelfLifePercent: props.minShelfLifePercent,
      today: momentTimezone().tz(LIVUP_TIMEZONE),
    });
    updateBoxes(restoredBoxes);
    if (restoredBoxes.length > 0) {
      enqueueSnackbar(`${restoredBoxes.length} etiquetas lidas antes foram recuperadas.`, { variant: 'info' });
    }
    if (droppedCount > 0) {
      showError(`${droppedCount} etiquetas lidas antes saíram da lista porque não passam mais na conferência.`);
    }
  }, []);

  const handleScannedCode = useStableCallback((scannedCode: string) => {
    scanInProgressRef.current = false;
    const code = normalizeBoxBarcode(sanitizeScannedCode(scannedCode));
    if (code.length < MIN_SCANNED_CODE_LENGTH || props.finishingPicking) {
      return;
    }
    setMissingModalOpen(false);
    const now = performance.now();
    if (isRepeatedScan(lastScanRef.current, code, now)) {
      lastScanRef.current = { code, at: now };
      enqueueSnackbar(REPEATED_SCAN_NOTICE, { variant: 'warning', autoHideDuration: SNACKBAR_DURATION_MS });
      return;
    }
    const result = scanBox({
      code,
      items: props.items,
      boxes,
      rejectedBatchCodes,
      minShelfLifePercent: props.minShelfLifePercent,
      today: momentTimezone().tz(LIVUP_TIMEZONE),
    });
    if (result.error) {
      setLastScanRefused(true);
      showError(result.error);
      return;
    }
    if (lastScanRefused) {
      setLastScanRefused(false);
    }
    lastScanRef.current = { code, at: now };
    updateBoxes([...boxes, { ...result.box, packageId: getNextPackageId(boxes) }]);
  });

  const handleForceCancel = useStableCallback(props.onForceCancel);

  useEffect(() => {
    const keypress$ = fromEvent<KeyboardEvent>(window, 'keypress').pipe(
      filter(event => !isEditableTarget(event.target)),
      tap(event => {
        if (SCANNED_CHARACTER_REGEX.test(event.key)) {
          scanInProgressRef.current = true;
          blurFocusedElement();
        }
      }),
      map(event => String.fromCharCode(event.keyCode)),
      share()
    );
    const subscription = keypress$
      .pipe(
        buffer(keypress$.pipe(debounceTime(SCAN_DEBOUNCE_MS))),
        map(characters => characters.join(''))
      )
      .subscribe(handleScannedCode);
    const handlePaste = (event: ClipboardEvent) => {
      if (!isEditableTarget(event.target)) {
        event.preventDefault();
        handleScannedCode(event.clipboardData.getData('text'));
      }
    };
    window.addEventListener('paste', handlePaste);
    window.addEventListener('unload', handleForceCancel, false);
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('paste', handlePaste);
      window.removeEventListener('unload', handleForceCancel, false);
    };
  }, [handleScannedCode, handleForceCancel]);

  const handleRemoveBox = (index: number) => {
    if (window.confirm(`Tirar a etiqueta ${boxes[index].code} desta separação?`)) {
      lastScanRef.current = null;
      updateBoxes(boxes.filter((_, boxIndex) => boxIndex !== index));
    }
    blurFocusedElement();
  };

  const handleJoinPreviousPackage = () => {
    if (!scanInProgressRef.current) {
      updateBoxes(joinLastBoxToPreviousPackage(boxes));
    }
    blurFocusedElement();
  };

  const finish = () => {
    if (scanInProgressRef.current) {
      return;
    }
    setMissingModalOpen(false);
    props.onFinish(buildBoxPickingFinishParams(props.items, boxes, props.productsBySku));
  };

  const handleFinishClick = () => {
    if (missingItems.length > 0) {
      setMissingModalOpen(true);
      return;
    }
    finish();
  };

  const handleCancelClick = () => {
    if (boxes.length > 0 && !window.confirm('Cancelar o picking apaga as etiquetas lidas deste pedido. Continuar?')) {
      return;
    }
    clearStoredBoxes(storageKey);
    props.onCancel();
  };

  const summaryTable = useMemo(
    () => (
      <>
        <SectionTitle>Produtos do pedido</SectionTitle>
        <Table>
          <thead>
            <tr>
              <th>Produto</th>
              <th>Pedido</th>
              <th>Separado</th>
              <th>Falta</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(item => (
              <tr key={item.sku}>
                <td>
                  {getProductName(item.sku)} ({item.sku})
                </td>
                <td>{item.orderedQuantity}</td>
                <td>{item.pickedQuantity}</td>
                <MissingCell missing={item.missingQuantity > 0}>{item.missingQuantity}</MissingCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    ),
    [summary, props.productsBySku]
  );

  const boxesTable = useMemo(
    () =>
      boxes.length > 0 && (
        <>
          <SectionTitle>Etiquetas lidas</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Caixa</th>
                <th>Etiqueta</th>
                <th>Produto</th>
                <th>Lote</th>
                <th>Unidades</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {boxes
                .map((box, index) => ({ box, index }))
                .reverse()
                .map(({ box, index }) => (
                  <tr key={index}>
                    <td>{packageNumbers[index]}</td>
                    <td>{box.code}</td>
                    <td>{box.sku}</td>
                    <td>{box.batch}</td>
                    <td>{box.quantity}</td>
                    <td>
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        disabled={props.finishingPicking}
                        onClick={() => handleRemoveBox(index)}
                      >
                        Tirar
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      ),
    [boxes, packageNumbers, props.finishingPicking]
  );

  const renderMissingModal = () => (
    <StyledModal open={missingModalOpen} onClose={() => setMissingModalOpen(false)} disableRestoreFocus>
      <ModalContainer>
        <ModalTitle>O pedido está incompleto</ModalTitle>
        <Table>
          <thead>
            <tr>
              <th>Produto</th>
              <th>Faltam</th>
            </tr>
          </thead>
          <tbody>
            {missingItems.map(item => (
              <tr key={item.sku}>
                <td>
                  {getProductName(item.sku)} ({item.sku})
                </td>
                <td>{item.missingQuantity}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <ModalButtons>
          <ActionButton size="large" variant="text" color="primary" onClick={() => setMissingModalOpen(false)}>
            Voltar à bipagem
          </ActionButton>
          <ActionButton size="large" variant="contained" color="secondary" onClick={finish}>
            Finalizar com faltante
          </ActionButton>
        </ModalButtons>
      </ModalContainer>
    </StyledModal>
  );

  return (
    <BoxPickingContainer
      title="Separação por caixa"
      isExpressDelivery={props.isExpressDelivery}
      isUrgent={props.isUrgent}
    >
      <ProgressText>
        Unidades separadas{' '}
        <b>
          {pickedTotal}/{orderedTotal}
        </b>{' '}
        em {packageCount} caixas{boxes.length !== packageCount ? ` e ${boxes.length} etiquetas` : ''}
      </ProgressText>
      <ScanArea>
        <ScanHint>Leia a etiqueta da caixa</ScanHint>
        {lastBox && (
          <LastBoxInfo>
            Última etiqueta: {getProductName(lastBox.sku)} ({lastBox.sku}) · {lastBox.quantity} unidades · lote{' '}
            {lastBox.batch} · caixa {packageCount}
          </LastBoxInfo>
        )}
        {lastBox && (
          <JoinPackageRow>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={props.finishingPicking || lastScanRefused || !joinTarget}
              onClick={handleJoinPreviousPackage}
            >
              Mesma caixa da anterior{joinTarget ? ` (${joinTarget.sku})` : ''}
            </Button>
          </JoinPackageRow>
        )}
      </ScanArea>
      {summaryTable}
      {boxesTable}
      {renderMissingModal()}
      <ButtonsContainer>
        <ActionButton
          size="large"
          variant="outlined"
          color="primary"
          disabled={props.finishingPicking}
          onClick={handleCancelClick}
        >
          Cancelar picking
        </ActionButton>
        {props.finishingPicking ? (
          <CircularProgressContainer>
            <CircularProgress size={30} />
          </CircularProgressContainer>
        ) : (
          <ActionButton size="large" variant="contained" color="secondary" onClick={handleFinishClick}>
            Finalizar picking
          </ActionButton>
        )}
      </ButtonsContainer>
    </BoxPickingContainer>
  );
};
